import { useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import Select, { GroupBase, SingleValue, StylesConfig } from 'react-select';
import { debounce } from 'lodash';
import SearchIcon from '../Icons/SearchIcon';
interface ISearchSelectBoxProps {
  value: string;
  onChange: (selectedOption: SingleValue<ISelectOption>) => void;
  fetchOptions?: (inputValue: string) => Promise<ISelectOption[]>;
  loading?: boolean;
  isAsync: boolean;
  defaultFetch?: boolean;
  title: string;
  isUsingFilter: boolean;
  disable?: boolean;
  options?: ISelectOption[];
  error?: string;
  onMenuScrollToBottom?: () => void;
}
export interface ISelectOption {
  value: string;
  label: string;
  site?: string;
}
const SearchSelectBox = ({
  value,
  onChange,
  error,
  options = [],
  title = 'Select an option',
  disable = false,
  loading = false,
  isAsync = false,
  fetchOptions = () => Promise.resolve([]),
  defaultFetch = false,
  isUsingFilter = false,
  onMenuScrollToBottom,
}: ISearchSelectBoxProps) => {
  const [defaultOptions, setDefaultOptions] = useState<ISelectOption[]>([]);

  useEffect(() => {
    if (isAsync && defaultFetch && typeof fetchOptions === 'function') {
      fetchOptions('')
        .then((data) => setDefaultOptions(data))
        .catch((error) =>
          console.error('Error fetching default options:', error),
        );
    }
  }, [isAsync, defaultFetch]);

  const loadOptions = debounce(
    (inputValue: string, callback: (options: ISelectOption[]) => void) => {
      if (typeof fetchOptions !== 'function') {
        console.error('fetchOptions is not a function!');
        callback([]);
        return;
      }
      fetchOptions(inputValue)
        .then((results) => callback(results))
        .catch((error) => {
          console.error('Error fetching options:', error);
          callback([]);
        });
    },
    500,
  );

  const handleChange = (selectedOption: SingleValue<ISelectOption>) => {
    onChange(selectedOption);
  };

  const customStyles: StylesConfig<ISelectOption, false> = {
    control: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      borderColor: 'lightgray',
      boxShadow: 'none',
      '&:hover': { borderColor: '#3586f9' },
      color: 'black',
      width: isUsingFilter ? '200px' : '',
      paddingLeft: '30px',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      color: 'black',
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? '#e2e8f0' : 'white',
      color: 'black',
      '&:active': { backgroundColor: '#3586f9', color: 'white' },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'gray',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'black',
    }),
  };
  return (
    <div className={isUsingFilter ? '' : 'mb-3 lg:mb-5'}>
      <div className="position-relative">
        <span
          className="search-icon position-absolute"
          style={{
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            width: '17px',
            height: '17px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SearchIcon />
        </span>

        {isAsync ? (
          <AsyncSelect<ISelectOption, false, GroupBase<ISelectOption>>
            // cacheOptions
            classNames={{
              control: (state) => (state.isFocused ? 'w-100' : 'w-100'),
              option: (state) => (state.isSelected ? 'w-100' : 'w-100'),
              menu: () => 'w-100;',
            }}
            defaultOptions={options?.length > 0 ? options : defaultOptions}
            loadOptions={loadOptions}
            onChange={handleChange}
            placeholder={loading ? 'Loading...' : title}
            classNamePrefix="select"
            styles={customStyles}
            isDisabled={disable}
            isLoading={loading}
            isClearable={true}
            isSearchable={true}
            value={
              (options?.length > 0 ? options : defaultOptions)?.find(
                (option) => option.value === value,
              ) || null
            }
            onMenuScrollToBottom={onMenuScrollToBottom}
            onKeyDown={onMenuScrollToBottom}
          />
        ) : (
          <Select
            isSearchable={true}
            isClearable={true}
            options={options}
            onChange={handleChange}
            placeholder={loading ? 'Loading...' : title}
            name="select-box"
            value={options.find((option) => option?.value === value)}
            classNamePrefix="select"
            styles={customStyles}
            isDisabled={disable}
            isLoading={loading}
            onMenuScrollToBottom={onMenuScrollToBottom}
            onKeyDown={onMenuScrollToBottom}
          />
        )}
      </div>
      {error && <p className="form-text text-danger fs-5">{error}</p>}
    </div>
  );
};

export default SearchSelectBox;
